import { useMemo } from "react";
import { LINK_TYPES } from "features/PaymentLinks/modules/CreatePaymentLinkModal/constants/CreatePaymentLinkModal.constants";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { isNumericListingId, isMerchandise } from "utils/listing/listingUtils";

/**
 * Checks if an offering has a price of zero (free offering).
 * @param {Object} data - Offering data object
 * @returns {boolean} True if offering is free / zero price
 */
export const checkIsZeroPrice = (data = {}) => {
  if (!data) return false;
  if (data.is_free || data.isFree) return true;
  const candidates = [
    data.price_per_head,
    data.price,
    data.final_price,
    data.payable_amount,
    data.amount,
    data.discounted_price,
  ];
  const definedVal = candidates.find(
    (val) => val !== undefined && val !== null && val !== ""
  );
  if (definedVal !== undefined) {
    return Number(definedVal) === 0;
  }
  return false;
};

/**
 * Normalizes props and schema variations for offering data into a single adapter.
 * Handles prop naming variations (paymentLinkType, payment_link_type, isFlexible, is_flexible)
 * and offeringData schema variations into a unified domain model.
 *
 * @param {Object} props - Props passed to ShareListingPopup component
 * @returns {Object} Normalized offering attributes
 */
export const normalizeOfferingProps = (props = {}) => {
  const offeringData = props.offeringData || {};
  const offeringIdFromProps = props.offeringId;
  const rawListingId = offeringData.listing_id || offeringIdFromProps;

  const rawCurrency =
    props.currency ||
    props.currency_name ||
    offeringData.currency ||
    offeringData.currency_name ||
    offeringData.currency_code ||
    offeringData.metadata?.currency ||
    "";

  const currency = String(rawCurrency).toUpperCase().trim();

  const paymentLinkType =
    props.paymentLinkType ||
    props.payment_link_type ||
    offeringData.payment_link_type ||
    offeringData.link_type;

  const isFlexible = Boolean(
    props.isFlexible ||
      props.is_flexible ||
      paymentLinkType === LINK_TYPES.OPEN_LINK ||
      offeringData.is_flexible ||
      offeringData.isFlexible
  );

  const isTrafficAnalysis = Boolean(
    props.isTrafficAnalysis || offeringData.is_traffic_analysis
  );

  const hasListingId = Boolean(
    (props.hasListingId || offeringData.has_listing_id) &&
      isNumericListingId(rawListingId)
  );

  const isPlan = Boolean(
    props.isPlan ||
      props.is_plan ||
      offeringData.is_plan ||
      offeringData.isPlan ||
      offeringData.is_dropdown_plan
  );

  const isMerchandiseTypeOffering = Boolean(
    isMerchandise(offeringData.type) ||
      isMerchandise(offeringData.schedule_type) ||
      isMerchandise(offeringData.offering_type)
  );

  const hasMerchandiseVariants = Boolean(
    offeringData.is_offering_variants_enabled ||
      props.is_offering_variants_enabled ||
      !checkIsArrayEmpty(offeringData.variations) ||
      !checkIsArrayEmpty(offeringData.variants_display_data) ||
      !checkIsArrayEmpty(offeringData.variants) ||
      !checkIsArrayEmpty(offeringData.offering_variants)
  );

  const isMerchandiseWithVariants =
    isMerchandiseTypeOffering && hasMerchandiseVariants;

  const isZeroPrice = checkIsZeroPrice(offeringData);

  return {
    offeringData,
    rawListingId,
    currency,
    paymentLinkType,
    isFlexible,
    isTrafficAnalysis,
    hasListingId,
    isPlan,
    isMerchandiseTypeOffering,
    hasMerchandiseVariants,
    isMerchandiseWithVariants,
    isZeroPrice,
  };
};

/**
 * Custom React hook to normalize offering props with memoization.
 * @param {Object} props - Props passed to consumer components
 * @returns {Object} Memoized normalized offering domain model
 */
export const useNormalizedOffering = (props = {}) => {
  return useMemo(() => normalizeOfferingProps(props), [props]);
};
